import { useState, type MouseEvent } from 'react';
import {
    ArrowsClockwiseIcon,
    CalendarBlankIcon,
    ClockIcon,
    DotsThreeVerticalIcon,
    FolderOpenIcon,
    NotePencilIcon,
    TagIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { formatDate, formatRelativeTime } from '@/utils/date';
import { isHdmiViewport } from '@/utils/source';
import { formatDuration } from '@/utils/time';
import { isVideoUrl } from '@/utils/url';
import { SourceStatus, SourceType } from '@/types/source';
import {
    isAnalyzingSourceStatus,
    isSourceCardNavigationEnabled,
} from '@/features/SourceView/sourceStatusDisplay';
import { Card } from '@/components/Card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Button } from '@/components/ui/Button';
import { Image } from '../Image';
import type { ThumbnailQuickViewProps } from '../Thumbnail';
import { AnalysisBadge } from '../AnalysisBadge';
import { SourceTypeBadge } from '../SourceTypeBadge';
import { CameraThumbnail } from '../CameraThumbnail';
import { VideoThumbnail, VideoHoverPreview } from '../VideoThumbnail';
import { PendingBadge } from '../AnalysisBadge/PendingBadge';
import { FixingBadge } from '../AnalysisBadge/FixingBadge';
import { QueuedBadge } from '../AnalysisBadge/QueuedBadge';
import { ErrorBadge } from '../AnalysisBadge/ErrorBadge';
import { GeneratingPreviewBadge } from '../AnalysisBadge/GeneratingPreviewBadge';
import {
    usePipWatchingSource,
    usePipPlayerStore,
    type PipNavigationContext,
} from '@/stores/pip-player.store';

interface SourceCardProps {
    id: string;
    thumbnail: string;
    name: string;
    createdAt: number;
    duration: number;
    type: SourceType;
    canEdit: boolean;
    canDelete: boolean;
    isOwner: boolean;
    tags?: string[];
    checked?: boolean;
    onClick?: (id: string, event?: MouseEvent<HTMLDivElement>) => void;
    onDelete?: (id: string) => void;
    onEditSource?: (sourceId: string) => void;
    onReanalyzeSource?: (sourceId: string) => void;
    onManageFolders?: (sourceId: string) => void;
    analysisStatus?: SourceStatus;
    analysisProgress?: number;
    quickView?: ThumbnailQuickViewProps;
    quickViewNavigationContext?: PipNavigationContext;
}

export function SourceCard({
    id,
    thumbnail,
    name,
    createdAt,
    duration,
    type,
    canEdit,
    canDelete,
    isOwner,
    tags = [],
    checked,
    onClick,
    onDelete,
    onEditSource,
    onReanalyzeSource,
    onManageFolders,
    analysisStatus,
    analysisProgress,
    quickView,
    quickViewNavigationContext,
}: SourceCardProps) {
    const { t } = useTranslation([I18nNamespace.SOURCES, I18nNamespace.COMMON]);
    const [offline, setOffline] = useState(true);
    const [isHovered, setIsHovered] = useState(false);
    const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
    const isFile = type === SourceType.FILE;
    const isCamera = type === SourceType.CAMERA;
    const isWatching = usePipWatchingSource(id);
    const openSource = usePipPlayerStore((s) => s.openSource);

    const isVideoReady = isSourceCardNavigationEnabled(analysisStatus);

    const isProcessing = analysisStatus === SourceStatus.PROCESSING;

    const handleConnectionChange = (isOnline: boolean) => {
        setOffline(!isOnline);
    };

    const showEditSource = onEditSource && canEdit;
    const showManageFolders = onManageFolders && canEdit;
    const showReanalyzeSource = onReanalyzeSource && canEdit && isFile;
    const showDelete = onDelete && canDelete;
    const showActionsMenu =
        isOwner &&
        (showEditSource ||
            showManageFolders ||
            showReanalyzeSource ||
            showDelete);

    const resolvedQuickView: ThumbnailQuickViewProps | undefined =
        quickView ??
        (quickViewNavigationContext
            ? {
                  state: isWatching ? 'watching' : 'idle',
                  onClick: () =>
                      openSource({
                          sourceId: id,
                          navigationContext: quickViewNavigationContext,
                      }),
                  ariaLabel: t('common:button.quickView'),
              }
            : undefined);

    const thumbnailContent = (
        <div className="relative h-full w-full min-h-0 min-w-0">
            {isCamera ? (
                <CameraThumbnail
                    sourceId={id}
                    alt={`${name} live stream`}
                    className="h-full w-full object-cover"
                    liveOnHover={checked === undefined}
                    disabled={offline}
                    isHovered={checked === undefined ? isHovered : false}
                    onConnectionChange={handleConnectionChange}
                />
            ) : !isVideoReady ? (
                <div className="flex h-full w-full items-center justify-center bg-grey-700">
                    <span className="text-grey-300 text-sm">
                        {t('card.generatingPreview')}
                    </span>
                </div>
            ) : isVideoUrl(thumbnail) ? (
                <VideoThumbnail
                    key={id}
                    src={thumbnail}
                    className="pointer-events-none h-full w-full object-cover"
                    onLoad={() => setThumbnailLoaded(true)}
                    generatingPreview={isProcessing && !thumbnailLoaded}
                />
            ) : (
                <Image
                    key={id}
                    src={thumbnail}
                    alt={name}
                    auth
                    className="h-full w-full object-cover"
                    onLoad={() => setThumbnailLoaded(true)}
                    generatingPreview={isProcessing && !thumbnailLoaded}
                />
            )}
            {isFile &&
                analysisStatus === SourceStatus.COMPLETED &&
                checked === undefined && (
                    <VideoHoverPreview
                        sourceId={id}
                        duration={duration}
                        isHovered={isHovered}
                    />
                )}
        </div>
    );

    const topLeftSlot = (
        <>
            {isCamera && !offline && analysisStatus !== SourceStatus.ERROR && (
                <SourceTypeBadge type="camera" />
            )}
            {isCamera && analysisStatus === SourceStatus.ERROR && (
                <ErrorBadge />
            )}
            {isFile && analysisStatus === SourceStatus.COMPLETED && (
                <SourceTypeBadge type="file" />
            )}
            {isFile && analysisStatus === SourceStatus.PROCESSING && (
                <AnalysisBadge progress={analysisProgress ?? 0} />
            )}
            {isFile && analysisStatus === SourceStatus.QUEUED && (
                <QueuedBadge />
            )}
            {isFile && analysisStatus === SourceStatus.FIXING && (
                <FixingBadge />
            )}
            {isFile && analysisStatus === SourceStatus.PENDING && (
                <PendingBadge />
            )}
            {isFile && analysisStatus === SourceStatus.UPLOADING && (
                <GeneratingPreviewBadge />
            )}
            {isFile && analysisStatus === SourceStatus.ERROR && <ErrorBadge />}
        </>
    );

    const metadataItems = [
        {
            icon: CalendarBlankIcon,
            text: formatRelativeTime(createdAt, type),
            title: formatDate(createdAt),
            textClassName: 'lowercase first-letter:uppercase',
        },
        ...(isFile && duration > 0
            ? [
                  {
                      icon: ClockIcon,
                      text: formatDuration(duration),
                  },
              ]
            : []),
        ...(tags.length > 0
            ? [
                  {
                      icon: TagIcon,
                      text: tags.join(', '),
                  },
              ]
            : []),
    ];

    return (
        <div
            data-testid="source-card"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={isVideoReady ? '' : 'cursor-default'}
        >
            <Card
                title={name}
                thumbnailMedia={thumbnailContent}
                thumbnailQuickView={resolvedQuickView}
                topLeftSlot={topLeftSlot}
                metadata={metadataItems}
                selectable={checked !== undefined}
                checked={checked ?? false}
                onCheckedChange={() => onClick?.(id)}
                sideAction={
                    checked === undefined && showActionsMenu ? (
                        <div
                            className="flex-shrink-0"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="small"
                                        shape="round"
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                        className="p-1"
                                        leadingIcon={
                                            <DotsThreeVerticalIcon
                                                className="w-5 h-5 text-grey-200 hover:text-white-100 transition-colors"
                                                weight="bold"
                                            />
                                        }
                                        aria-label={t('actions.sourceActions')}
                                    />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    side="bottom"
                                    align="end"
                                    sideOffset={5}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <DropdownMenuGroup>
                                        {showEditSource ? (
                                            <DropdownMenuItemBase
                                                onSelect={() =>
                                                    onEditSource(id)
                                                }
                                            >
                                                <NotePencilIcon
                                                    size={16}
                                                    weight="regular"
                                                    className="flex-shrink-0"
                                                />
                                                <span className="text-sm font-medium">
                                                    {t('actions.editSource')}
                                                </span>
                                            </DropdownMenuItemBase>
                                        ) : null}
                                        {showManageFolders ? (
                                            <DropdownMenuItemBase
                                                onSelect={() =>
                                                    onManageFolders(id)
                                                }
                                            >
                                                <FolderOpenIcon
                                                    size={16}
                                                    weight="regular"
                                                    className="flex-shrink-0"
                                                />
                                                <span className="text-sm font-medium">
                                                    {t('actions.manageFolders')}
                                                </span>
                                            </DropdownMenuItemBase>
                                        ) : null}
                                        {showReanalyzeSource ? (
                                            <DropdownMenuItemBase
                                                disabled={
                                                    analysisStatus
                                                        ? isAnalyzingSourceStatus(
                                                              analysisStatus,
                                                          )
                                                        : false
                                                }
                                                onSelect={() =>
                                                    onReanalyzeSource(id)
                                                }
                                            >
                                                <ArrowsClockwiseIcon
                                                    size={16}
                                                    weight="regular"
                                                    className="flex-shrink-0"
                                                />
                                                <span className="text-sm font-medium">
                                                    {t('actions.reanalyze')}
                                                </span>
                                            </DropdownMenuItemBase>
                                        ) : null}
                                        {showDelete ? (
                                            <DropdownMenuItemBase
                                                variant="danger"
                                                disabled={isHdmiViewport(id)}
                                                onSelect={() => onDelete(id)}
                                            >
                                                <TrashIcon
                                                    size={16}
                                                    weight="regular"
                                                    className="flex-shrink-0"
                                                />
                                                <span className="text-sm font-medium">
                                                    {t('actions.deleteSource')}
                                                </span>
                                            </DropdownMenuItemBase>
                                        ) : null}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : null
                }
                onClick={
                    isVideoReady ? (event) => onClick?.(id, event) : undefined
                }
            />
        </div>
    );
}
